// A REGRA do Plano de Estudos Inteligente (000382): quais aulas cabem no tempo que o aluno tem hoje.
//
// É a regra do app da Lovable (`getClassesForTime`, em `src/data/cfcClassesApi.ts` de lá), sem mudar
// nada do que o aluno via: as aulas PENDENTES, na ordem do plano; a primeira entra SEMPRE, mesmo que
// passe do tempo (senão quem tem 30 minutos e a próxima aula de 41 nunca sairia do lugar); depois entram
// as seguintes enquanto a soma couber; e a primeira que não cabe PARA a lista — não se pula uma aula
// longa para encaixar uma curta mais adiante, porque a ordem é o plano.
//
// Puro: nada de rede, de relógio nem de React. A ordem vem pronta do banco (`bonus_minhas_aulas`, que
// ordena pela `ordem` do plano), e aqui não se reordena nada.
import type { AulaBonus } from "./aulasBonus";

/** Os tempos que o aluno escolhe, em minutos — os oito do app de lá. */
export const TEMPOS_DO_DIA = [30, 60, 90, 120, 150, 180, 210, 240] as const;
export type TempoDoDia = (typeof TEMPOS_DO_DIA)[number];

/** O padrão: uma hora. A Lovable abria sem escolha; aqui a tela abre com a lista já montada. */
export const TEMPO_PADRAO: TempoDoDia = 60;

/** O tempo do endereço (`?tempo=90`), ou o padrão quando ele não é um dos oito. */
export function tempoDaBusca(valor: string | null): TempoDoDia {
  const n = Number(valor);
  return (TEMPOS_DO_DIA as readonly number[]).includes(n) ? (n as TempoDoDia) : TEMPO_PADRAO;
}

/** "30 min", "1 h", "1 h 30", "4 h" — como o rótulo do botão. */
export function rotuloDoTempo(minutos: number): string {
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

export type AulasDeHoje = {
  aulas: AulaBonus[];
  /** A soma das durações das aulas escolhidas, em segundos. */
  segundos: number;
  /** A primeira aula, sozinha, já passa do tempo escolhido. A tela diz isso, em vez de esconder. */
  primeiraPassaDoTempo: boolean;
};

/**
 * As próximas aulas pendentes que cabem em `minutos`.
 *
 * Aula sem duração conta como zero: ela cabe sempre. A carga recusa aula sem duração, então isso só
 * acontece com uma aula que alguém cadastre à mão — e aí escondê-la seria pior que deixá-la entrar.
 */
export function aulasParaHoje(aulas: AulaBonus[], minutos: number): AulasDeHoje {
  const limite = Math.max(0, minutos) * 60;
  const escolhidas: AulaBonus[] = [];
  let soma = 0;
  for (const a of aulas) {
    if (a.concluidaEm !== null) continue;
    const d = Math.max(0, a.duracaoSegundos ?? 0);
    if (escolhidas.length === 0) {
      escolhidas.push(a);
      soma += d;
      continue;
    }
    if (soma + d > limite) break;
    escolhidas.push(a);
    soma += d;
  }
  return {
    aulas: escolhidas,
    segundos: soma,
    primeiraPassaDoTempo: escolhidas.length > 0 && Math.max(0, escolhidas[0].duracaoSegundos ?? 0) > limite,
  };
}

/** Quanto o aluno já estudou: a soma das durações das aulas concluídas, em segundos. */
export function segundosEstudados(aulas: AulaBonus[]): number {
  return aulas
    .filter((a) => a.concluidaEm !== null)
    .reduce((s, a) => s + Math.max(0, a.duracaoSegundos ?? 0), 0);
}
